import React, { useState, useMemo } from 'react';
import { logger } from '@/lib/logger';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useAppStore } from '@/stores/appStore';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Search, Download, Eye, Trash2, X, Zap, FileText } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
// Lazy load jsPDF (saves 450KB from initial bundle)
import { loadJsPDF } from '@/utils/exportLazyLoader';

interface LoadsViewProps {
  className?: string;
  onViewCircuit?: (circuitId: string) => void;
  onDeleteCircuit?: (circuitId: string) => Promise<boolean>;
}

export function LoadsView({ className, onViewCircuit, onDeleteCircuit }: LoadsViewProps) {
  const { data, floorPlan } = useFloorPlanContext();
  const { circuits, fixtures, controls } = data;
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteCircuitId, setDeleteCircuitId] = useState<string | null>(null);

  // Get AI tools confidence threshold from app store
  const confidenceThreshold = useAppStore(state => state.aiTools.confidenceThreshold);

  // Filter fixtures by confidence threshold (only show fixtures that meet the threshold)
  const visibleFixtures = useMemo(() => {
    return fixtures.filter(f => {
      const confidence = f.confidence_score || 1;
      return confidence >= confidenceThreshold;
    });
  }, [fixtures, confidenceThreshold]);

  // Group visible fixtures by circuit
  const fixturesByCircuit = useMemo(() => {
    return circuits.reduce(
      (acc, circuit) => {
        acc[circuit.id] = visibleFixtures.filter(f => f.circuit_id === circuit.id);
        return acc;
      },
      {} as Record<string, typeof fixtures>,
    );
  }, [circuits, visibleFixtures]);

  // Calculate circuit data with fixture counts
  const circuitData = useMemo(() => {
    return circuits.map(circuit => ({
      ...circuit,
      fixtureCount: fixturesByCircuit[circuit.id]?.length || 0,
    }));
  }, [circuits, fixturesByCircuit]);

  // Filter circuits based on search
  const filteredCircuits = useMemo(() => {
    if (!searchQuery) return circuitData;

    const query = searchQuery.toLowerCase();
    return circuitData.filter(
      circuit =>
        circuit.name.toLowerCase().includes(query) ||
        circuit.sequence_number?.toString().includes(query) ||
        circuit.description?.toLowerCase().includes(query),
    );
  }, [circuitData, searchQuery]);

  // Sort circuits by sequence number
  const sortedCircuits = useMemo(() => {
    return [...filteredCircuits].sort((a, b) => {
      const aNum = a.sequence_number || 0;
      const bNum = b.sequence_number || 0;
      return aNum - bNum;
    });
  }, [filteredCircuits]);

  // Calculate control type summary
  const controlSummary = useMemo(() => {
    const summary = {
      keypad: 0,
      dimmer: 0,
      switch: 0,
      auxiliary: 0,
      'centralized-keypad': 0,
      total: 0,
    };

    controls.forEach(control => {
      if (control.type in summary) {
        summary[control.type as keyof typeof summary]++;
      } else {
        summary.auxiliary++;
      }
      summary.total++;
    });

    return summary;
  }, [controls]);

  // Export to CSV function
  const handleExportCSV = () => {
    try {
      // Create CSV content with headers
      let csvContent = 'Load Name,Type,Description\n';

      // Add circuit data
      sortedCircuits.forEach(circuit => {
        const name = circuit.sequence_number
          ? `${circuit.sequence_number} - ${circuit.name}`
          : circuit.name;
        // Add empty type and description as per original format
        csvContent += `"${name}",,\n`;
      });

      // Create a Blob with the CSV content
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

      // Create a download link and trigger the download
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `loads_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('CSV exported successfully');
    } catch (error) {
      logger.error('Error exporting to CSV:', error);
      toast.error('Failed to export to CSV');
    }
  };

  // Export to PDF function
  const handleExportPDF = async () => {
    toast.info('Generating PDF...');

    try {
      // Load jsPDF lazily (only when exporting)
      const { jsPDF } = await loadJsPDF();

      // Create a new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;

      // Add header
      let yPosition = margin;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Load Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      // Add floor plan name
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Floor Plan: ${floorPlan.name}`, margin, yPosition);
      yPosition += 10;

      // Add date
      const date = new Date().toLocaleDateString();
      pdf.text(`Date: ${date}`, margin, yPosition);
      yPosition += 15;

      // Table headers
      const tableHeaders = ['Load #', 'Load Name', 'Fixtures'];
      const tableWidths = [25, 120, 25];
      const rowHeight = 10;

      // Draw table header
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPosition, pageWidth - margin * 2, rowHeight, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);

      let xOffset = margin;
      tableHeaders.forEach((header, index) => {
        pdf.text(header, xOffset + 3, yPosition + 7);
        xOffset += tableWidths[index];
      });

      yPosition += rowHeight;

      // Draw table rows
      pdf.setFont('helvetica', 'normal');

      sortedCircuits.forEach((circuit, index) => {
        // Check if we need a new page
        if (yPosition > pageHeight - margin * 2) {
          pdf.addPage();
          yPosition = margin;

          // Re-add table headers on new page
          pdf.setFillColor(240, 240, 240);
          pdf.rect(margin, yPosition, pageWidth - margin * 2, rowHeight, 'F');
          pdf.setFont('helvetica', 'bold');

          xOffset = margin;
          tableHeaders.forEach((header, idx) => {
            pdf.text(header, xOffset + 3, yPosition + 7);
            xOffset += tableWidths[idx];
          });

          yPosition += rowHeight;
          pdf.setFont('helvetica', 'normal');
        }

        // Alternating row background
        if (index % 2 === 0) {
          pdf.setFillColor(250, 250, 250);
          pdf.rect(margin, yPosition, pageWidth - margin * 2, rowHeight, 'F');
        }

        // Draw borders
        pdf.setDrawColor(220, 220, 220);
        pdf.rect(margin, yPosition, pageWidth - margin * 2, rowHeight);

        // Draw cell contents
        xOffset = margin;

        // Load #
        pdf.text(circuit.sequence_number?.toString() || '-', xOffset + 3, yPosition + 7);
        xOffset += tableWidths[0];

        // Load Name
        const circuitName = getCircuitNameWithoutNumber(circuit.name, circuit.sequence_number);
        const maxWidth = tableWidths[1] - 6;

        if (pdf.getTextWidth(circuitName) > maxWidth) {
          let truncatedName = circuitName;
          while (pdf.getTextWidth(truncatedName + '...') > maxWidth && truncatedName.length > 0) {
            truncatedName = truncatedName.slice(0, -1);
          }
          pdf.text(truncatedName + '...', xOffset + 3, yPosition + 7);
        } else {
          pdf.text(circuitName, xOffset + 3, yPosition + 7);
        }
        xOffset += tableWidths[1];

        // Fixtures Count
        const fixtureCount = circuit.fixtureCount.toString();
        pdf.text(fixtureCount, xOffset + tableWidths[2] - 8, yPosition + 7, { align: 'right' });

        yPosition += rowHeight;
      });

      // Add summary section - check for enough space
      if (yPosition + 80 < pageHeight - margin) {
        yPosition += 15;

        // Load Summary section with white background and border
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(220, 220, 220);
        pdf.rect(margin, yPosition, pageWidth - margin * 2, 100, 'D');

        // Load Summary header
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Load Summary', margin + 5, yPosition + 10);

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text('Overview of loads and fixtures in this project', margin + 5, yPosition + 18);
        pdf.setTextColor(0, 0, 0);

        yPosition += 25;

        // Load stats boxes - smaller and cleaner
        const statBoxWidth = (pageWidth - margin * 2 - 10) / 3;
        const statBoxHeight = 25;

        // Total Loads box
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(220, 220, 220);
        pdf.rect(margin + 5, yPosition, statBoxWidth - 5, statBoxHeight, 'D');
        pdf.setFontSize(7);
        pdf.setTextColor(100, 100, 100);
        pdf.text('TOTAL LOADS', margin + 5 + (statBoxWidth - 5) / 2, yPosition + 8, {
          align: 'center',
        });
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(circuits.length.toString(), margin + 5 + (statBoxWidth - 5) / 2, yPosition + 18, {
          align: 'center',
        });

        // Total Fixtures box
        pdf.setFillColor(255, 255, 255);
        pdf.rect(margin + statBoxWidth + 2.5, yPosition, statBoxWidth - 5, statBoxHeight, 'D');
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text(
          'TOTAL FIXTURES',
          margin + statBoxWidth + 2.5 + (statBoxWidth - 5) / 2,
          yPosition + 8,
          { align: 'center' },
        );
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(
          visibleFixtures.length.toString(),
          margin + statBoxWidth + 2.5 + (statBoxWidth - 5) / 2,
          yPosition + 18,
          { align: 'center' },
        );

        // Unassigned box
        pdf.setFillColor(255, 255, 255);
        pdf.rect(margin + statBoxWidth * 2, yPosition, statBoxWidth - 5, statBoxHeight, 'D');
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text('UNASSIGNED', margin + statBoxWidth * 2 + (statBoxWidth - 5) / 2, yPosition + 8, {
          align: 'center',
        });
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(
          visibleFixtures.filter(f => !f.circuit_id).length.toString(),
          margin + statBoxWidth * 2 + (statBoxWidth - 5) / 2,
          yPosition + 18,
          { align: 'center' },
        );

        yPosition += statBoxHeight + 10;

        // Control Devices section
        if (controlSummary.total > 0) {
          // Divider line
          pdf.setDrawColor(220, 220, 220);
          pdf.line(margin + 5, yPosition, pageWidth - margin - 5, yPosition);
          yPosition += 8;

          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0);
          pdf.text('Control Devices', margin + 5, yPosition);
          yPosition += 8;

          // Control boxes - 6 in one row, smaller
          const controlBoxWidth = (pageWidth - margin * 2 - 15) / 6;
          const controlBoxHeight = 20;

          // All controls in one row
          const controlTypes = [
            { label: 'Total Controls', value: controlSummary.total },
            { label: 'Dimmers', value: controlSummary.dimmer },
            { label: 'Keypads', value: controlSummary.keypad },
            { label: 'Centralized/LV', value: controlSummary['centralized-keypad'] },
            { label: 'Switches', value: controlSummary.switch },
            { label: 'Auxiliary', value: controlSummary.auxiliary },
          ];

          controlTypes.forEach((control, index) => {
            const xPos = margin + 5 + (controlBoxWidth + 2) * index;

            // Draw box with white background
            pdf.setFillColor(255, 255, 255);
            pdf.setDrawColor(220, 220, 220);
            pdf.rect(xPos, yPosition, controlBoxWidth - 2, controlBoxHeight, 'D');

            // Value on top
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0);
            pdf.text(control.value.toString(), xPos + (controlBoxWidth - 2) / 2, yPosition + 8, {
              align: 'center',
            });

            // Label below
            pdf.setFontSize(6);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(100, 100, 100);
            pdf.text(control.label, xPos + (controlBoxWidth - 2) / 2, yPosition + 15, {
              align: 'center',
            });
          });
        }
      } else {
        // If not enough space, add summary on new page
        pdf.addPage();
        yPosition = margin;

        // Load Summary section with white background and border
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(220, 220, 220);
        pdf.rect(margin, yPosition, pageWidth - margin * 2, 100, 'D');

        // Load Summary header
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Load Summary', margin + 5, yPosition + 10);

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text('Overview of loads and fixtures in this project', margin + 5, yPosition + 18);
        pdf.setTextColor(0, 0, 0);

        yPosition += 25;

        // Load stats boxes - smaller and cleaner
        const statBoxWidth = (pageWidth - margin * 2 - 10) / 3;
        const statBoxHeight = 25;

        // Total Loads box
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(220, 220, 220);
        pdf.rect(margin + 5, yPosition, statBoxWidth - 5, statBoxHeight, 'D');
        pdf.setFontSize(7);
        pdf.setTextColor(100, 100, 100);
        pdf.text('TOTAL LOADS', margin + 5 + (statBoxWidth - 5) / 2, yPosition + 8, {
          align: 'center',
        });
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(circuits.length.toString(), margin + 5 + (statBoxWidth - 5) / 2, yPosition + 18, {
          align: 'center',
        });

        // Total Fixtures box
        pdf.setFillColor(255, 255, 255);
        pdf.rect(margin + statBoxWidth + 2.5, yPosition, statBoxWidth - 5, statBoxHeight, 'D');
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text(
          'TOTAL FIXTURES',
          margin + statBoxWidth + 2.5 + (statBoxWidth - 5) / 2,
          yPosition + 8,
          { align: 'center' },
        );
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(
          visibleFixtures.length.toString(),
          margin + statBoxWidth + 2.5 + (statBoxWidth - 5) / 2,
          yPosition + 18,
          { align: 'center' },
        );

        // Unassigned box
        pdf.setFillColor(255, 255, 255);
        pdf.rect(margin + statBoxWidth * 2, yPosition, statBoxWidth - 5, statBoxHeight, 'D');
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text('UNASSIGNED', margin + statBoxWidth * 2 + (statBoxWidth - 5) / 2, yPosition + 8, {
          align: 'center',
        });
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(
          visibleFixtures.filter(f => !f.circuit_id).length.toString(),
          margin + statBoxWidth * 2 + (statBoxWidth - 5) / 2,
          yPosition + 18,
          { align: 'center' },
        );

        yPosition += statBoxHeight + 10;

        // Control Devices section
        if (controlSummary.total > 0) {
          // Divider line
          pdf.setDrawColor(220, 220, 220);
          pdf.line(margin + 5, yPosition, pageWidth - margin - 5, yPosition);
          yPosition += 8;

          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0);
          pdf.text('Control Devices', margin + 5, yPosition);
          yPosition += 8;

          // Control boxes - 6 in one row, smaller
          const controlBoxWidth = (pageWidth - margin * 2 - 15) / 6;
          const controlBoxHeight = 20;

          // All controls in one row
          const controlTypes = [
            { label: 'Total Controls', value: controlSummary.total },
            { label: 'Dimmers', value: controlSummary.dimmer },
            { label: 'Keypads', value: controlSummary.keypad },
            { label: 'Centralized/LV', value: controlSummary['centralized-keypad'] },
            { label: 'Switches', value: controlSummary.switch },
            { label: 'Auxiliary', value: controlSummary.auxiliary },
          ];

          controlTypes.forEach((control, index) => {
            const xPos = margin + 5 + (controlBoxWidth + 2) * index;

            // Draw box with white background
            pdf.setFillColor(255, 255, 255);
            pdf.setDrawColor(220, 220, 220);
            pdf.rect(xPos, yPosition, controlBoxWidth - 2, controlBoxHeight, 'D');

            // Value on top
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0);
            pdf.text(control.value.toString(), xPos + (controlBoxWidth - 2) / 2, yPosition + 8, {
              align: 'center',
            });

            // Label below
            pdf.setFontSize(6);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(100, 100, 100);
            pdf.text(control.label, xPos + (controlBoxWidth - 2) / 2, yPosition + 15, {
              align: 'center',
            });
          });
        }
      }

      // Save the PDF
      pdf.save(
        `Load_Report_${floorPlan.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
      );

      toast.success('PDF generated successfully');
    } catch (error) {
      logger.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  // Get circuit name without sequence number
  const getCircuitNameWithoutNumber = (name: string, sequenceNumber?: number) => {
    if (sequenceNumber) {
      const prefix = `${sequenceNumber}. `;
      if (name.startsWith(prefix)) {
        return name.substring(prefix.length);
      }
    }
    return name;
  };

  // Handle delete circuit
  const handleDeleteCircuit = async () => {
    if (!deleteCircuitId || !onDeleteCircuit) return;

    const success = await onDeleteCircuit(deleteCircuitId);
    if (success) {
      toast.success('Circuit deleted successfully');
    } else {
      toast.error('Failed to delete circuit');
    }
    setDeleteCircuitId(null);
  };

  // Handle view circuit
  const handleViewCircuit = (circuitId: string) => {
    if (onViewCircuit) {
      onViewCircuit(circuitId);
    }
  };

  return (
    <div className={className}>
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="p-6 pb-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle>Load Report</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleExportCSV}>
                    <Download className="h-4 w-4 mr-2" />
                    Export to CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleExportPDF}>
                    <FileText className="h-4 w-4 mr-2" />
                    Export to PDF
                  </Button>
                </div>
              </div>

              {/* Search section */}
              <div className="flex gap-3 pt-4">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search loads..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => e.stopPropagation()}
                    onKeyUp={e => e.stopPropagation()}
                    className="pl-8"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full rounded-l-none"
                      onClick={() => setSearchQuery('')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>

        {/* Table */}
        <div className="flex-1 px-6 pb-6 overflow-hidden">
          <Card className="h-full flex flex-col">
            <ScrollArea className="flex-1">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Load #</TableHead>
                    <TableHead>Load Name</TableHead>
                    <TableHead className="w-[80px] text-center">Fixtures</TableHead>
                    <TableHead>Floor Plan</TableHead>
                    <TableHead className="w-[120px] text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedCircuits.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-32 text-center">
                        <div className="flex flex-col items-center gap-2 text-muted-foreground">
                          <Zap className="h-8 w-8" />
                          <p>No circuits found</p>
                          {searchQuery && <p className="text-sm">Try adjusting your search</p>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedCircuits.map(circuit => {
                      const circuitToDelete =
                        deleteCircuitId === circuit.id
                          ? circuits.find(c => c.id === circuit.id)
                          : null;

                      return (
                        <TableRow key={circuit.id}>
                          <TableCell className="font-medium">
                            {circuit.sequence_number || '-'}
                          </TableCell>
                          <TableCell>
                            {getCircuitNameWithoutNumber(circuit.name, circuit.sequence_number)}
                          </TableCell>
                          <TableCell className="text-center">{circuit.fixtureCount}</TableCell>
                          <TableCell>{floorPlan.name}</TableCell>
                          <TableCell>
                            <div className="flex justify-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleViewCircuit(circuit.id)}
                                title="View circuit"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setDeleteCircuitId(circuit.id)}
                                title="Delete circuit"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </Card>
        </div>

        {/* Summary Section */}
        <div className="px-6 pb-6">
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/80 dark:to-slate-800/80 rounded-lg border shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-muted/30">
              <h3 className="text-lg font-semibold">Load Summary</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Overview of loads and fixtures in this project
              </p>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {/* Load summary stats */}
                <div className="col-span-2 sm:col-span-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 py-2">
                    <div className="flex-1 bg-background rounded-lg p-4 border shadow-sm">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1">
                        Total Loads
                      </div>
                      <div className="text-3xl font-bold text-primary">{circuits.length}</div>
                    </div>
                    <div className="flex-1 bg-background rounded-lg p-4 border shadow-sm">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1">
                        Total Fixtures
                      </div>
                      <div className="text-3xl font-bold text-primary">
                        {visibleFixtures.length}
                      </div>
                    </div>
                    <div className="flex-1 bg-background rounded-lg p-4 border shadow-sm">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1">
                        Unassigned
                      </div>
                      <div className="text-3xl font-bold text-primary">
                        {visibleFixtures.filter(f => !f.circuit_id).length}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Control devices section */}
                {controlSummary.total > 0 && (
                  <div className="col-span-2 sm:col-span-4 border-t pt-4 mt-2">
                    <h4 className="text-sm font-medium mb-3">Control Devices</h4>

                    <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                      <div className="bg-background/70 p-3 rounded-md border text-center col-span-2 sm:col-span-1">
                        <div className="text-xl font-semibold">{controlSummary.total}</div>
                        <div className="text-xs text-muted-foreground mt-1">Total Controls</div>
                      </div>
                      <div className="bg-background/70 p-3 rounded-md border text-center">
                        <div className="text-xl font-semibold">{controlSummary.dimmer}</div>
                        <div className="text-xs text-muted-foreground mt-1">Dimmers</div>
                      </div>
                      <div className="bg-background/70 p-3 rounded-md border text-center">
                        <div className="text-xl font-semibold">{controlSummary.keypad}</div>
                        <div className="text-xs text-muted-foreground mt-1">Keypads</div>
                      </div>
                      <div className="bg-background/70 p-3 rounded-md border text-center">
                        <div className="text-xl font-semibold">
                          {controlSummary['centralized-keypad']}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">Centralized/LV</div>
                      </div>
                      <div className="bg-background/70 p-3 rounded-md border text-center">
                        <div className="text-xl font-semibold">{controlSummary.switch}</div>
                        <div className="text-xs text-muted-foreground mt-1">Switches</div>
                      </div>
                      <div className="bg-background/70 p-3 rounded-md border text-center">
                        <div className="text-xl font-semibold">{controlSummary.auxiliary}</div>
                        <div className="text-xs text-muted-foreground mt-1">Auxiliary</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteCircuitId} onOpenChange={() => setDeleteCircuitId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Circuit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this circuit? This action cannot be undone.
            </AlertDialogDescription>
            {deleteCircuitId &&
              (() => {
                const circuit = circuits.find(c => c.id === deleteCircuitId);
                const fixtureCount = fixturesByCircuit[deleteCircuitId]?.length || 0;
                return circuit ? (
                  <div className="mt-3 space-y-1 text-sm">
                    <div className="font-medium text-foreground">{circuit.name}</div>
                    {fixtureCount > 0 && (
                      <div className="text-muted-foreground">
                        This will unassign {fixtureCount} fixture{fixtureCount !== 1 ? 's' : ''}{' '}
                        from this circuit.
                      </div>
                    )}
                  </div>
                ) : null;
              })()}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCircuit}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
